import {
  LoaderCircle,
  Plus,
  Save,
  ShieldCheck,
  Trash2,
} from 'lucide-react'
import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import {
  cargarBodegasActivas,
  cargarPesajesCompra,
  cargarTiposEnvase,
  guardarRecepcion,
  validarRecepcion,
} from '../../services/abastecimiento'
import { validarKilosAceptados } from '../../utils/abastecimiento'
import {
  calcularPesaje,
  formatearKilosCompra,
  milesimasAKilos,
  nombreProveedorCompra,
  normalizarCantidadCajas,
  pesoAMilesimas,
  pesoPalletAMilesimas,
  taraAMilesimas,
} from '../../utils/compras'
import { obtenerFechaActualPos } from '../../utils/pos'
import ComparacionKilosCompraPrevia from './ComparacionKilosCompraPrevia'
import { calcularDiferenciaKilos } from './diferenciaCompraPrevia'
import ModalBase, {
  AvisoModal,
  CampoModal,
  estiloInputModal,
} from './ModalBase'

function crearFilaPesaje(tipoEnvaseId = '', pesoPallet = '0') {
  return {
    clave: `${Date.now()}-${Math.random()}`,
    cantidadCajas: '',
    tipoEnvaseId,
    pesoPallet,
    pesoBruto: '',
    observacion: '',
  }
}

function formatearPesoEditable(valor) {
  return String(valor ?? '').replace('.', ',')
}

function RecepcionModal({
  abierto,
  compra,
  recepcion,
  empresaId,
  puedeValidar,
  onCerrar,
  onGuardada,
}) {
  const esPrevia = compra?.modalidad === 'PREVIA'

  const [cargando, setCargando] = useState(false)
  const [errorCarga, setErrorCarga] = useState('')
  const [bodegas, setBodegas] = useState([])
  const [tiposEnvase, setTiposEnvase] = useState([])
  const [pesajesDirecta, setPesajesDirecta] = useState([])

  const [bodegaId, setBodegaId] = useState('')
  const [fechaRecepcion, setFechaRecepcion] = useState(obtenerFechaActualPos())
  const [filas, setFilas] = useState([])
  const [observacionCalidad, setObservacionCalidad] = useState('')

  const [validando, setValidando] = useState(false)
  const [kilosAceptados, setKilosAceptados] = useState('')
  const [motivoRechazo, setMotivoRechazo] = useState('')

  const [procesando, setProcesando] = useState(false)
  const [error, setError] = useState('')
  const enCursoRef = useRef(false)
  const recepcionIdRef = useRef(null)

  useEffect(() => {
    if (!abierto || !compra || !empresaId) return

    let cancelado = false

    const cargar = async () => {
      setCargando(true)
      setErrorCarga('')
      setError('')
      setValidando(false)
      setMotivoRechazo('')
      enCursoRef.current = false
      recepcionIdRef.current = recepcion?.id || null

      try {
        const [listaBodegas, envases, pesajes] = await Promise.all([
          cargarBodegasActivas(empresaId),
          esPrevia ? cargarTiposEnvase(empresaId) : Promise.resolve([]),
          cargarPesajesCompra(empresaId, compra.id),
        ])

        if (cancelado) return

        setBodegas(listaBodegas)
        setTiposEnvase(envases)

        // No existe una configuración de bodega predeterminada: solo se
        // propone la bodega del borrador, o la única activa si hay una.
        const bodegaBorrador = listaBodegas.find(
          (bodega) => bodega.id === recepcion?.bodega_id
        )

        setBodegaId(
          bodegaBorrador?.id ||
            (listaBodegas.length === 1 ? listaBodegas[0].id : '')
        )
        setFechaRecepcion(
          recepcion?.fecha_recepcion || obtenerFechaActualPos()
        )
        setObservacionCalidad(recepcion?.observacion_calidad || '')

        if (esPrevia) {
          setPesajesDirecta([])
          setFilas(
            pesajes.length > 0
              ? pesajes.map((pesaje) => ({
                  clave: pesaje.id,
                  cantidadCajas: String(pesaje.cantidad_cajas),
                  tipoEnvaseId: pesaje.tipo_envase_id || '',
                  pesoPallet: formatearPesoEditable(
                    pesaje.pallet_tara_kg ?? 0
                  ),
                  pesoBruto: formatearPesoEditable(pesaje.peso_bruto_kg),
                  observacion: pesaje.observacion || '',
                }))
              : [crearFilaPesaje(envases.length === 1 ? envases[0].id : '')]
          )
        } else {
          setFilas([])
          setPesajesDirecta(pesajes)
        }

        setKilosAceptados(
          formatearPesoEditable(Number(compra.peso_neto_kg) || '')
        )
      } catch (errorAlCargar) {
        console.error('Error al cargar la recepción:', errorAlCargar)

        if (!cancelado) {
          setErrorCarga(
            errorAlCargar.message ||
              'No fue posible cargar los datos de la recepción.'
          )
        }
      } finally {
        if (!cancelado) setCargando(false)
      }
    }

    cargar()

    return () => {
      cancelado = true
    }
  }, [abierto, compra, recepcion, empresaId, esPrevia])

  const envasesPorId = useMemo(
    () => new Map(tiposEnvase.map((tipo) => [tipo.id, tipo])),
    [tiposEnvase]
  )

  // Cálculo en vivo por fila (vista previa; el backend recalcula).
  const calculos = useMemo(
    () =>
      filas.map((fila) => {
        const tipo = envasesPorId.get(fila.tipoEnvaseId)
        const cantidadCajas = normalizarCantidadCajas(fila.cantidadCajas)
        const pesoBrutoMilesimas = pesoAMilesimas(fila.pesoBruto)

        const taraPalletMilesimas = pesoPalletAMilesimas(fila.pesoPallet)

        if (
          !tipo ||
          !Number.isFinite(cantidadCajas) ||
          !Number.isFinite(pesoBrutoMilesimas) ||
          !Number.isFinite(taraPalletMilesimas)
        ) {
          return null
        }

        return {
          cantidadCajas,
          pesoBrutoMilesimas,
          ...calcularPesaje({
            cantidadCajas,
            taraUnitariaMilesimas: taraAMilesimas(tipo.tara_kg),
            pesoBrutoMilesimas,
            taraPalletMilesimas,
          }),
        }
      }),
    [filas, envasesPorId]
  )

  const netoMilesimas = esPrevia
    ? calculos.reduce(
        (suma, calculo) => suma + (calculo?.pesoNetoMilesimas || 0),
        0
      )
    : taraAMilesimas(compra?.peso_neto_kg ?? 0)

  const netoKg = milesimasAKilos(netoMilesimas)

  // El bruto es solo informativo; la comparación con lo acordado usa el neto.
  const brutoMilesimas = esPrevia
    ? calculos.reduce(
        (suma, calculo) => suma + (calculo?.pesoBrutoMilesimas || 0),
        0
      )
    : 0

  // Informativa: se recalcula con cada pesaje; no se envía al backend.
  const comparacionKilos = calcularDiferenciaKilos({
    modalidad: compra?.modalidad,
    acordadoKg: compra?.cantidad_acordada_kg,
    brutoKg: milesimasAKilos(brutoMilesimas),
    netoKg,
    taraKg: milesimasAKilos(brutoMilesimas - netoMilesimas),
    estadoRecepcion: recepcion?.estado || 'BORRADOR',
    enEdicion: true,
  })

  if (!abierto || !compra) return null

  const actualizarFila = (clave, campo, valor) => {
    setFilas((actuales) =>
      actuales.map((fila) =>
        fila.clave === clave ? { ...fila, [campo]: valor } : fila
      )
    )
    setError('')
  }

  const agregarFila = () => {
    setFilas((actuales) => [
      ...actuales,
      crearFilaPesaje(
        actuales[actuales.length - 1]?.tipoEnvaseId || '',
        actuales[actuales.length - 1]?.pesoPallet ?? '0'
      ),
    ])
  }

  const quitarFila = (clave) => {
    setFilas((actuales) => actuales.filter((fila) => fila.clave !== clave))
  }

  const construirPesajes = () => {
    if (!esPrevia) return { pesajes: null }

    if (filas.length === 0) {
      return { error: 'Debe ingresar al menos un pesaje.' }
    }

    const pesajes = []

    for (let indice = 0; indice < filas.length; indice += 1) {
      const calculo = calculos[indice]
      const fila = filas[indice]
      const numero = indice + 1

      if (!Number.isFinite(normalizarCantidadCajas(fila.cantidadCajas))) {
        return {
          error: `Pesaje ${numero}: la cantidad de cajas debe ser un entero mayor a cero.`,
        }
      }

      if (!envasesPorId.has(fila.tipoEnvaseId)) {
        return { error: `Pesaje ${numero}: seleccione una Caja/Rejilla.` }
      }

      if (!Number.isFinite(pesoPalletAMilesimas(fila.pesoPallet))) {
        return {
          error: `Pesaje ${numero}: peso del pallet no válido (entre 0 y 10.000 kg, máximo 3 decimales).`,
        }
      }

      if (!Number.isFinite(pesoAMilesimas(fila.pesoBruto))) {
        return {
          error: `Pesaje ${numero}: peso bruto no válido (máximo 3 decimales).`,
        }
      }

      if (!calculo || calculo.pesoNetoMilesimas <= 0) {
        return {
          error: `Pesaje ${numero}: el peso bruto debe superar la tara (cajas y pallet).`,
        }
      }

      pesajes.push({
        cantidad_cajas: calculo.cantidadCajas,
        tipo_envase_id: fila.tipoEnvaseId,
        peso_pallet_kg: milesimasAKilos(
          pesoPalletAMilesimas(fila.pesoPallet)
        ),
        peso_bruto_kg: milesimasAKilos(calculo.pesoBrutoMilesimas),
        observacion: fila.observacion.trim() || null,
      })
    }

    return { pesajes }
  }

  const guardarBorrador = async () => {
    const { error: errorPesajes, pesajes } = construirPesajes()

    if (errorPesajes) throw new Error(errorPesajes)

    if (!bodegaId) throw new Error('Debe seleccionar una bodega.')

    if (!fechaRecepcion) throw new Error('Debe ingresar la fecha de recepción.')

    const id = await guardarRecepcion({
      empresaId,
      compraId: compra.id,
      bodegaId,
      fecha: fechaRecepcion,
      pesajes,
      observacionCalidad: observacionCalidad.trim(),
    })

    recepcionIdRef.current = id

    return id
  }

  const ejecutar = async (accion) => {
    // Protege contra doble clic: el estado de React no se actualiza a
    // tiempo para frenar un segundo click inmediato.
    if (enCursoRef.current) return

    enCursoRef.current = true
    setProcesando(true)
    setError('')

    try {
      await accion()
    } catch (errorAccion) {
      console.error('Error en recepción:', errorAccion)
      setError(errorAccion.message || 'Ocurrió un error en la recepción.')
    } finally {
      enCursoRef.current = false
      setProcesando(false)
    }
  }

  const alGuardarBorrador = () =>
    ejecutar(async () => {
      await guardarBorrador()
      onGuardada('Borrador de recepción guardado.')
    })

  const alValidar = () =>
    ejecutar(async () => {
      const resultado = validarKilosAceptados({
        kilosAceptados,
        netoKg,
        motivoRechazo,
      })

      if (resultado.error) throw new Error(resultado.error)

      const recepcionId = await guardarBorrador()

      await validarRecepcion({
        empresaId,
        recepcionId,
        kilosAceptados: resultado.kilos,
        motivoRechazo: motivoRechazo.trim(),
        observacionCalidad: observacionCalidad.trim(),
      })

      onGuardada(
        resultado.rechazoTotal
          ? 'Recepción rechazada totalmente. No se generó entrada a inventario.'
          : `Recepción validada. Ingresaron ${formatearKilosCompra(
              resultado.kilos
            )} a inventario.`
      )
    })

  const aceptadoCero =
    validando &&
    Number.isFinite(pesoAMilesimas(kilosAceptados)) &&
    pesoAMilesimas(kilosAceptados) === 0

  return (
    <ModalBase
      abierto={abierto}
      titulo={`Recepción · ${compra.codigo_compra}`}
      tituloId="recepcion-titulo"
      procesando={procesando}
      anchoMaximo="max-w-3xl"
      onCerrar={onCerrar}
    >
      <p className="mt-2 text-sm text-slate-500">
        {nombreProveedorCompra(compra)} · {compra.producto_nombre}
        {esPrevia
          ? ` · acordado ${formatearKilosCompra(compra.cantidad_acordada_kg)}`
          : ''}
      </p>

      {cargando ? (
        <div className="flex min-h-48 items-center justify-center">
          <LoaderCircle
            size={32}
            className="animate-spin text-emerald-600"
          />
        </div>
      ) : errorCarga ? (
        <div className="mt-5">
          <AvisoModal>{errorCarga}</AvisoModal>
        </div>
      ) : (
        <div className="mt-5 space-y-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <CampoModal
              label="Bodega de destino"
              htmlFor="recepcion-bodega"
              requerido
            >
              <select
                id="recepcion-bodega"
                value={bodegaId}
                onChange={(event) => setBodegaId(event.target.value)}
                disabled={procesando}
                className={estiloInputModal}
              >
                <option value="">Seleccionar bodega</option>
                {bodegas.map((bodega) => (
                  <option
                    key={bodega.id}
                    value={bodega.id}
                  >
                    {bodega.nombre}
                  </option>
                ))}
              </select>

              {bodegas.length === 0 && (
                <p className="mt-2 text-sm text-amber-700">
                  No hay bodegas activas. Créalas en Bodega → Bodegas.
                </p>
              )}
            </CampoModal>

            <CampoModal
              label="Fecha de recepción"
              htmlFor="recepcion-fecha"
              requerido
            >
              <input
                id="recepcion-fecha"
                type="date"
                value={fechaRecepcion}
                onChange={(event) => setFechaRecepcion(event.target.value)}
                disabled={procesando}
                className={estiloInputModal}
              />
            </CampoModal>
          </div>

          {/* PESAJES */}
          <section>
            <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">
              {esPrevia
                ? `Pesajes de la recepción (${filas.length})`
                : `Pesajes de la compra (${pesajesDirecta.length})`}
            </h3>

            {esPrevia ? (
              <div className="space-y-3">
                {filas.map((fila, indice) => {
                  const calculo = calculos[indice]

                  return (
                    <div
                      key={fila.clave}
                      className="rounded-xl border border-slate-200 p-3 sm:p-4"
                    >
                      <div className="mb-3 flex items-center justify-between">
                        <p className="text-xs font-bold uppercase tracking-wide text-slate-400">
                          Pesaje {indice + 1}
                        </p>

                        {filas.length > 1 && (
                          <button
                            type="button"
                            onClick={() => quitarFila(fila.clave)}
                            disabled={procesando}
                            aria-label={`Eliminar pesaje ${indice + 1}`}
                            className="rounded-lg p-2 text-red-600 transition hover:bg-red-50 disabled:opacity-60"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>

                      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                        <CampoModal
                          label="Cajas"
                          htmlFor={`cajas-${fila.clave}`}
                        >
                          <input
                            id={`cajas-${fila.clave}`}
                            type="text"
                            inputMode="numeric"
                            value={fila.cantidadCajas}
                            onChange={(event) =>
                              actualizarFila(
                                fila.clave,
                                'cantidadCajas',
                                event.target.value
                              )
                            }
                            placeholder="8"
                            disabled={procesando}
                            className={estiloInputModal}
                          />
                        </CampoModal>

                        <CampoModal
                          label="Peso bruto (kg)"
                          htmlFor={`bruto-${fila.clave}`}
                        >
                          <input
                            id={`bruto-${fila.clave}`}
                            type="text"
                            inputMode="decimal"
                            value={fila.pesoBruto}
                            onChange={(event) =>
                              actualizarFila(
                                fila.clave,
                                'pesoBruto',
                                event.target.value
                              )
                            }
                            placeholder="125,5"
                            disabled={procesando}
                            className={estiloInputModal}
                          />
                        </CampoModal>

                        <CampoModal
                          label="Caja/Rejilla"
                          htmlFor={`envase-${fila.clave}`}
                          className="col-span-2"
                        >
                          <select
                            id={`envase-${fila.clave}`}
                            value={fila.tipoEnvaseId}
                            onChange={(event) =>
                              actualizarFila(
                                fila.clave,
                                'tipoEnvaseId',
                                event.target.value
                              )
                            }
                            disabled={procesando}
                            className={estiloInputModal}
                          >
                            <option value="">Seleccionar</option>
                            {tiposEnvase.map((tipo) => (
                              <option
                                key={tipo.id}
                                value={tipo.id}
                              >
                                {tipo.nombre} · tara{' '}
                                {formatearKilosCompra(tipo.tara_kg)}
                              </option>
                            ))}
                          </select>
                        </CampoModal>

                        <CampoModal
                          label="Peso del pallet (kg)"
                          htmlFor={`pallet-${fila.clave}`}
                          className="col-span-2 md:col-span-4"
                        >
                          <input
                            id={`pallet-${fila.clave}`}
                            type="text"
                            inputMode="decimal"
                            value={fila.pesoPallet}
                            onChange={(event) =>
                              actualizarFila(
                                fila.clave,
                                'pesoPallet',
                                event.target.value
                              )
                            }
                            placeholder="0"
                            disabled={procesando}
                            className={estiloInputModal}
                          />
                        </CampoModal>

                        <CampoModal
                          label="Observación"
                          htmlFor={`obs-${fila.clave}`}
                          className="col-span-2 md:col-span-3"
                        >
                          <input
                            id={`obs-${fila.clave}`}
                            type="text"
                            value={fila.observacion}
                            onChange={(event) =>
                              actualizarFila(
                                fila.clave,
                                'observacion',
                                event.target.value
                              )
                            }
                            placeholder="Opcional"
                            maxLength={500}
                            disabled={procesando}
                            className={estiloInputModal}
                          />
                        </CampoModal>

                        <div className="col-span-2 md:col-span-1">
                          <p className="mb-2 text-sm font-semibold text-slate-700">
                            Neto
                          </p>

                          <p
                            className={`flex min-h-12 items-center rounded-lg border px-3 font-semibold tabular-nums ${
                              calculo && calculo.pesoNetoMilesimas <= 0
                                ? 'border-red-200 bg-red-50 text-red-600'
                                : 'border-slate-200 bg-slate-50 text-slate-900'
                            }`}
                          >
                            {calculo
                              ? formatearKilosCompra(
                                  milesimasAKilos(calculo.pesoNetoMilesimas)
                                )
                              : '—'}
                          </p>
                        </div>
                      </div>
                    </div>
                  )
                })}

                <button
                  type="button"
                  onClick={agregarFila}
                  disabled={procesando}
                  className="flex min-h-12 w-full items-center justify-center gap-2 rounded-lg border border-emerald-600 px-4 py-3 text-sm font-semibold text-emerald-700 transition hover:bg-emerald-50 disabled:opacity-60"
                >
                  <Plus size={18} />
                  Agregar pesaje
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <AvisoModal tipo="info">
                  Compra directa: se reutilizan los pesajes ya registrados.
                  No se vuelven a ingresar.
                </AvisoModal>

                {pesajesDirecta.map((pesaje) => (
                  <div
                    key={pesaje.id}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm"
                  >
                    <span className="font-semibold text-slate-700">
                      {pesaje.numero_pesaje}. {pesaje.cantidad_cajas} cajas ·{' '}
                      {pesaje.envase_nombre}
                      {Number(pesaje.pallet_tara_kg) > 0 &&
                        ` · Pallet ${formatearKilosCompra(
                          pesaje.pallet_tara_kg
                        )}`}
                    </span>

                    <span className="font-bold tabular-nums text-emerald-700">
                      {formatearKilosCompra(pesaje.peso_neto_kg)} netos
                    </span>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-3 flex items-center justify-between gap-4 rounded-xl bg-emerald-50 px-4 py-3">
              <span className="text-sm font-bold uppercase tracking-wide text-emerald-800">
                Kilos netos recibidos
              </span>

              <span className="text-lg font-bold tabular-nums text-emerald-700">
                {formatearKilosCompra(netoKg)}
              </span>
            </div>
          </section>

          {esPrevia && (
            <ComparacionKilosCompraPrevia
              comparacion={comparacionKilos}
              compacta
            />
          )}

          <CampoModal
            label="Observación de calidad"
            htmlFor="recepcion-calidad"
          >
            <textarea
              id="recepcion-calidad"
              value={observacionCalidad}
              onChange={(event) => setObservacionCalidad(event.target.value)}
              rows={2}
              maxLength={1000}
              placeholder="Opcional"
              disabled={procesando}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-3 text-base outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 disabled:bg-slate-100"
            />
          </CampoModal>

          {/* VALIDACIÓN */}
          {puedeValidar && validando && (
            <section className="space-y-4 rounded-xl border border-emerald-200 bg-emerald-50/50 p-4">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-emerald-800">
                Validar recepción
              </h3>

              <CampoModal
                label={`Kilos aceptados (máx. ${formatearKilosCompra(netoKg)})`}
                htmlFor="recepcion-aceptados"
                requerido
              >
                <input
                  id="recepcion-aceptados"
                  type="text"
                  inputMode="decimal"
                  value={kilosAceptados}
                  onChange={(event) => {
                    setKilosAceptados(event.target.value)
                    setError('')
                  }}
                  disabled={procesando}
                  className={estiloInputModal}
                />
              </CampoModal>

              {aceptadoCero && (
                <CampoModal
                  label="Motivo del rechazo total"
                  htmlFor="recepcion-motivo"
                  requerido
                >
                  <textarea
                    id="recepcion-motivo"
                    value={motivoRechazo}
                    onChange={(event) => setMotivoRechazo(event.target.value)}
                    rows={2}
                    maxLength={1000}
                    disabled={procesando}
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-3 text-base outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 disabled:bg-slate-100"
                  />
                </CampoModal>
              )}

              <AvisoModal tipo="info">
                {aceptadoCero
                  ? 'Rechazo total: no se genera entrada a inventario.'
                  : 'Al validar se registra una única entrada a inventario con los kilos aceptados. Esta acción no se repite.'}
              </AvisoModal>
            </section>
          )}

          {error && <AvisoModal>{error}</AvisoModal>}

          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onCerrar}
              disabled={procesando}
              className="min-h-12 w-full rounded-lg border border-slate-300 px-5 py-3 font-semibold text-slate-700 transition hover:bg-slate-100 disabled:opacity-60 sm:w-auto"
            >
              Cancelar
            </button>

            <button
              type="button"
              onClick={alGuardarBorrador}
              disabled={procesando}
              className="flex min-h-12 w-full items-center justify-center gap-2 rounded-lg border border-emerald-600 px-5 py-3 font-semibold text-emerald-700 transition hover:bg-emerald-50 disabled:opacity-60 sm:w-auto"
            >
              <Save size={18} />
              Guardar borrador
            </button>

            {puedeValidar &&
              (validando ? (
                <button
                  type="button"
                  onClick={alValidar}
                  disabled={procesando}
                  className="flex min-h-12 w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 px-5 py-3 font-semibold text-white transition hover:bg-emerald-700 disabled:bg-emerald-400 sm:w-auto"
                >
                  {procesando ? (
                    <LoaderCircle
                      size={18}
                      className="animate-spin"
                    />
                  ) : (
                    <ShieldCheck size={18} />
                  )}
                  {aceptadoCero ? 'Confirmar rechazo' : 'Confirmar validación'}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    // Propone aceptar todo lo recibido; el operador ajusta.
                    if (!kilosAceptados.trim() && netoMilesimas > 0) {
                      setKilosAceptados(formatearPesoEditable(netoKg))
                    }

                    setValidando(true)
                  }}
                  disabled={procesando}
                  className="flex min-h-12 w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 px-5 py-3 font-semibold text-white transition hover:bg-emerald-700 disabled:bg-emerald-400 sm:w-auto"
                >
                  <ShieldCheck size={18} />
                  Validar
                </button>
              ))}
          </div>
        </div>
      )}
    </ModalBase>
  )
}

export default RecepcionModal
